import api from '@/services/api2.service';
import { MOCK_TRANSCRIPTS, MOCK_SUMMARIES } from '@/services/mocks/transcript-summary.mock';

// --- AI summary prototype mocks (ENT-3361) ---
// Same response shape as the axios calls ({ data: { result } }), so the real API can replace them as is.

const MOCK_LATENCY_MS = 400;
const MOCK_SUMMARY_STEP_MS = 1500;

const mockSummaries = new Map();
const mockFailedOnce = new Set();

const mockResponse = (result) => ({ data: { result } });

function mockDelay(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
    });
}

function findMockTranscript(conference_id) {
    const transcript = MOCK_TRANSCRIPTS.find(item => item.conference_id === conference_id);
    if (!transcript) {
        throw new Error(`Mock transcript not found: ${conference_id}`);
    }
    return transcript;
}

const firstSentence = (text) => (text.match(/^.*?[.!?](\s|$)/) || [text])[0].trim();

// Fallback for sessions without a hand-written summary: pick lines from the transcript itself.
function buildMockSummary(transcript) {
    const { messages } = transcript;
    const pick = (count, from = 0) => messages.slice(from, from + count);

    return {
        key_takeaways: pick(3).map(msg => ({
            title: firstSentence(msg.source),
            text: msg.source.slice(firstSentence(msg.source).length).trim(),
            start: msg.time,
        })),
        speaker_highlights: pick(2, 1).map(msg => ({
            title: `${msg.display_name}:`,
            text: firstSentence(msg.source),
            start: msg.time,
        })),
        follow_up_questions: [
            { text: `What is one idea from ${transcript.title} worth sharing with your team?`, start: messages[0].time },
            { text: 'Which point from the session needs a follow-up conversation?', start: messages[messages.length - 1].time },
        ],
    };
}

class TranscriptService {
    
    getTranscriptList(token){
        return api
            .get('/transcripts', { 
                headers: { 
                    Authorization: token
                } 
            });
    }

    deleteTranscript(conference_id, token) {
        return api
            .delete(`/transcript/${conference_id}`, {
                headers: {
                    Authorization: token
                }
            });
    }

    getTranscript(conference_id, token) {
        return api
            .get(`/transcript/${conference_id}`, {
                headers: {
                    Authorization: token
                }
            });
    }

    getSharedTranscript(conference_id) {
        return api
            .get(`/transcript/shared/${conference_id}`, {
            });
    }

    updateTranscript(transcript, token) {
        console.log(transcript)
        return api
            .patch(`/transcript/${transcript.id}`, {
                title: transcript.title,
                is_shared: transcript.is_shared
            }, {
                headers: {
                    Authorization: token
                },
            });
    }

    async getTranscriptListMock() {
        await mockDelay(MOCK_LATENCY_MS);
        const transcripts = MOCK_TRANSCRIPTS.map(({ conference_id, title, room_type, timestamp, duration_minutes }) => ({
            conference_id, title, room_type, timestamp, duration_minutes
        }));
        return mockResponse({ transcripts });
    }

    async getTranscriptMock(conference_id) {
        await mockDelay(MOCK_LATENCY_MS);
        const transcript = { ...findMockTranscript(conference_id) };
        delete transcript.mock_fail_first_summary;
        return mockResponse({ ...transcript, messages: [...transcript.messages] });
    }

    // Resolves with `summary: null` when nothing has been generated for the session yet.
    async getTranscriptSummaryMock(conference_id) {
        await mockDelay(MOCK_LATENCY_MS);
        return mockResponse({ summary: mockSummaries.get(conference_id) || null });
    }

    // onProgress receives 'transcript_ready', 'highlights' and 'questions' as generation moves on.
    // Rejects with an AbortError when `signal` is aborted.
    async generateTranscriptSummaryMock(conference_id, { signal, onProgress } = {}) {
        const transcript = findMockTranscript(conference_id);

        onProgress?.('transcript_ready');
        await mockDelay(MOCK_SUMMARY_STEP_MS, signal);

        if (transcript.mock_fail_first_summary && !mockFailedOnce.has(conference_id)) {
            mockFailedOnce.add(conference_id);
            throw new Error('summary_service_unavailable');
        }

        onProgress?.('highlights');
        await mockDelay(MOCK_SUMMARY_STEP_MS, signal);
        onProgress?.('questions');
        await mockDelay(MOCK_SUMMARY_STEP_MS, signal);

        const summary = {
            ...(MOCK_SUMMARIES[conference_id] || buildMockSummary(transcript)),
            generated_at: new Date().toISOString(),
        };
        mockSummaries.set(conference_id, summary);
        return mockResponse({ summary });
    }

    // value: 'helpful' | 'not_helpful' | null
    async sendTranscriptSummaryFeedbackMock(conference_id, value) {
        await mockDelay(MOCK_LATENCY_MS);
        const summary = mockSummaries.get(conference_id);
        if (summary) {
            summary.feedback = value;
        }
        return mockResponse({ feedback: value });
    }
}
export default new TranscriptService();